'use client';

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Upload, User, Users, UserPlus, ChevronLeft, ChevronRight, ChevronDown,
  BookOpen, CheckCircle2, Clock, X, Camera, Trash2, Loader2,
  GraduationCap, Headphones, BarChart3, CalendarDays,
  Pencil, Save, Ban, AlertTriangle, Route, Target, Layers,
  Download, FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Progress } from '@/components/ui/Progress';
import { Switch } from '@/components/ui/Switch';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import {
  InstitutionStyledTemporalInput,
  parseInstitutionStoredDateToInputValue,
} from '@/components/institution/InstitutionStyledTemporalInput';
import {
  InstitutionConfiguredGroupsList,
  parseInstitutionGroupsApiResponse,
  type InstitutionConfiguredGroupRow,
} from '@/components/institution/InstitutionConfiguredGroupsList';
import { computeInfpcAgeFromBirthDateYmd } from '@/lib/infpc-age-from-birth';
import { institutionLearnerDetailPath } from '@/lib/institution-tab-registry';

type LearnerFlow = {
  flowId: string;
  title: string;
  completed: boolean;
  startedAt?: string | null;
  completedAt?: string | null;
  lastAccessAt?: string | null;
  totalTimeSpent?: number;
  totalBlocks?: number;
  blocksVisited?: number;
  sessionsCompleted?: number;
  averageScore?: number | null;
};


type LearnerRow = {
  id: string;
  firstName?: string;
  lastName?: string;
  name: string;
  email: string;
  group: string;
  progress: number;
  enrollments: number;
  completedCount: number;
  totalTimeSpent: number;
  lastActive: string;
  status: string;
  facePhotoUrl?: string | null;
  infpcGender?: string | null;
  infpcCsp?: string | null;
  infpcHasDiploma?: boolean | null;
  infpcSeniorityYears?: number | null;
  infpcAge?: number | null;
  infpcIsSalariePlus?: boolean | null;
  infpcSalariePlusType?: number | null;
  infpcHireDate?: string | null;
  infpcBirthDate?: string | null;
  /** Libellé affiché (service référencié ou ancien champ service libre). */
  service?: string | null;
  institutionServiceId?: string | null;
  companyArrivalDate?: string | null;
  /** Membres des groupes paramétrés (`PATCH` avec `learningGroupIds`). */
  learningGroupIds?: string[];
  flows?: LearnerFlow[];
};

type InfpcFormState = {
  gender: '' | 'M' | 'F';
  csp: string;
  hasDiploma: boolean;
  seniority: string;
  isSalariePlus: boolean;
  salariePlusType: '' | '1' | '2';
  hireDate: string;
  birthDate: string;
};

type EnterpriseServiceOption = { id: string; name: string };
type CsvImportRow = Record<string, unknown>;

function normalizeHeaderKey(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function infpcFormFromLearner(learner: LearnerRow): InfpcFormState {
  const g = learner.infpcGender;
  return {
    gender: g === 'M' || g === 'F' ? g : '',
    csp: learner.infpcCsp ?? '',
    hasDiploma: Boolean(learner.infpcHasDiploma),
    seniority: learner.infpcSeniorityYears != null ? String(learner.infpcSeniorityYears) : '',
    isSalariePlus: Boolean(learner.infpcIsSalariePlus),
    salariePlusType:
      learner.infpcSalariePlusType === 1 || learner.infpcSalariePlusType === 2
        ? String(learner.infpcSalariePlusType)
        : '',
    hireDate: parseInstitutionStoredDateToInputValue(learner.infpcHireDate ?? ''),
    birthDate: parseInstitutionStoredDateToInputValue(learner.infpcBirthDate ?? ''),
  };
}

const PAGE_SIZE = 10;

/** Clés acceptées par `PATCH /institution/learners/:id` (évite d’envoyer `group`, `service`, etc.). */
const INSTITUTION_LEARNER_PATCH_KEYS = new Set([
  'firstName',
  'lastName',
  'email',
  'status',
  'infpcGender',
  'infpcCsp',
  'infpcHasDiploma',
  'infpcSeniorityYears',
  'infpcIsSalariePlus',
  'infpcSalariePlusType',
  'infpcHireDate',
  'infpcBirthDate',
  'institutionServiceId',
  'companyArrivalDate',
  'learningGroupIds',
]);

function sanitizeInstitutionLearnerPatch(
  data: Record<string, string | number | boolean | null | undefined | string[]>,
): Record<string, string | number | boolean | null | string[]> {
  const out: Record<string, string | number | boolean | null | string[]> = {};
  for (const key of INSTITUTION_LEARNER_PATCH_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(data, key) || data[key] === undefined) continue;
    const v = data[key];
    if (key === 'learningGroupIds' && Array.isArray(v)) {
      out[key] = [...new Set(v.map((id) => String(id).trim()).filter(Boolean))];
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) {
      out[key] = v;
    }
  }
  return out;
}

const INFPC_CSP_VALUES = [
  'manual_worker',
  'employee',
  'technician',
  'supervisor',
  'executive',
  'director',
] as const;

const statusBadge = (s: string) => {
  const map: Record<string, 'success' | 'warning' | 'info'> = { active: 'success', inactive: 'warning', completed: 'info' };
  return map[s] ?? 'default';
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h${String(m % 60).padStart(2, '0')}`;
}

/** Parcours, formations, podcasts et indicateurs de progression — placés sous la ligne (comme le tableau formateur). */
function LearnerActivityInlineExpanded({ learner }: { learner: LearnerRow }) {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const [expandedFlowId, setExpandedFlowId] = useState<string | null>(null);

  useEffect(() => {
    setExpandedFlowId(null);
  }, [learner.id]);

  const allFlows = learner.flows ?? [];

  return (
    <div className="border-t border-[#F0F4FA] bg-[#F8FAFC]/60 px-4 py-4 ps-14 dark:border-[#19335E]/50 dark:bg-[#122644]/30 sm:px-6 sm:ps-16">
      {learner.lastActive && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-[#5B6472] shadow-sm dark:bg-[#122644]/80 dark:text-[#DCE7F7]">
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#5B6472]/70" />
          {t('institution.learners.lastActivity')}{' '}
          {new Date(learner.lastActive).toLocaleDateString(dateLocale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </div>
      )}
      <div className="mb-4 rounded-lg bg-white p-4 shadow-sm dark:bg-[#122644]/80">
        <div className="mb-3 flex flex-wrap gap-3 text-xs font-semibold uppercase tracking-wider text-[#5B6472]/70 dark:text-[#B6CAEC]">
          <span className="inline-flex items-center gap-1">
            <BookOpen className="h-3.5 w-3.5 text-blue-500" />
            {learner.completedCount}/{learner.enrollments}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            {formatDuration(learner.totalTimeSpent)}
          </span>
        </div>
        <Progress value={learner.progress} max={100} size="md" showLabel />
      </div>

      <div className="mb-4 rounded-lg border border-[#E8EDF5] bg-white p-4 shadow-sm dark:border-[#19335E] dark:bg-[#122644]/80">
        <div className="mb-3 flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-blue-500" />
          <h4 className="text-sm font-semibold text-[#0C1C38] dark:text-white">
            {t('institution.learners.flows')} ({allFlows.length})
          </h4>
        </div>
        {allFlows.length === 0 ? (
          <p className="text-xs text-[#5B6472]/70 italic">{t('institution.learners.noFlowsEnrolled')}</p>
        ) : (
          <div className="space-y-2">
            {allFlows.map((f) => {
              const open = expandedFlowId === f.flowId;
              const blockProg =
                (f.totalBlocks ?? 0) > 0
                  ? Math.round(((f.blocksVisited ?? 0) / (f.totalBlocks ?? 1)) * 100)
                  : 0;
              return (
                <div
                  key={f.flowId}
                  className="overflow-hidden rounded-lg border border-[#F0F4FA] bg-[#F8FAFC]/50 dark:border-[#122644] dark:bg-[#122644]/60"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedFlowId(open ? null : f.flowId)}
                    className="flex w-full items-center gap-2 p-3 text-start transition-colors hover:bg-[#F0F4FA]/80 dark:hover:bg-[#19335E]/30"
                  >
                    <ChevronDown
                      className={cn('h-3.5 w-3.5 shrink-0 text-[#5B6472]/70 transition-transform', open && 'rotate-180')}
                    />
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-[#0C1C38] dark:text-[#DCE7F7]">
                      {f.title}
                    </p>
                    <Badge variant={f.completed ? 'success' : 'warning'} className="shrink-0 text-[10px]">
                      {f.completed ? t('institution.learners.flowDone') : t('institution.learners.inProgress')}
                    </Badge>
                  </button>
                  {open && (
                    <div className="space-y-2.5 border-t border-[#F0F4FA] bg-white px-3 py-3 dark:border-[#19335E] dark:bg-[#122644]/80">
                      <Progress value={blockProg} max={100} size="sm" />
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                          <Layers className="h-3.5 w-3.5 text-blue-400" />
                          <span>
                            {t('institution.learners.blocks', {
                              visited: f.blocksVisited ?? 0,
                              total: f.totalBlocks ?? 0,
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                          <Clock className="h-3.5 w-3.5 text-amber-400" />
                          <span>{formatDuration(f.totalTimeSpent ?? 0)}</span>
                        </div>
                        {f.averageScore != null && (
                          <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                            <Target className="h-3.5 w-3.5 text-pink-400" />
                            <span>{t('institution.learners.score', { score: f.averageScore })}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#244585]" />
                          <span>{t('institution.learners.sessions', { count: f.sessionsCompleted ?? 0 })}</span>
                        </div>
                        {f.startedAt && (
                          <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                            <CalendarDays className="h-3.5 w-3.5 text-[#5B6472]/70" />
                            <span>
                              {t('institution.learners.started')}{' '}
                              {new Date(f.startedAt).toLocaleDateString(dateLocale)}
                            </span>
                          </div>
                        )}
                        {f.completedAt && (
                          <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                            <CalendarDays className="h-3.5 w-3.5 text-[#244585]" />
                            <span>
                              {t('institution.learners.ended')}{' '}
                              {new Date(f.completedAt).toLocaleDateString(dateLocale)}
                            </span>
                          </div>
                        )}
                        {f.lastAccessAt && (
                          <div className="flex items-center gap-2 text-xs text-[#5B6472] dark:text-[#DCE7F7]">
                            <CalendarDays className="h-3.5 w-3.5 text-blue-400" />
                            <span>
                              {t('institution.learners.lastAccess')}{' '}
                              {new Date(f.lastAccessAt).toLocaleDateString(dateLocale)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E8EDF5] bg-white p-4 shadow-sm dark:border-[#19335E] dark:bg-[#122644]/80">
        <div className="mb-3 flex items-center gap-2">
          <Headphones className="h-4 w-4 text-purple-500" />
          <h4 className="text-sm font-semibold text-[#0C1C38] dark:text-white">{t('institution.learners.podcasts')}</h4>
        </div>
        <p className="text-xs text-[#5B6472]/70 italic">{t('institution.learners.noPodcastListen')}</p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Confirmation Modal
   ──────────────────────────────────────────────────────────────────────────── */
function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  danger,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-[#E8EDF5] bg-white p-6 shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38]">
        <div className="mb-4 flex items-start gap-3">
          <div className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            danger ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30',
          )}>
            <AlertTriangle className={cn('h-5 w-5', danger ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#0C1C38] dark:text-white">{title}</h3>
            <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{description}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={loading}>{t('institution.common.cancel')}</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
            disabled={loading}
            leftIcon={loading ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Detail Panel
   ──────────────────────────────────────────────────────────────────────────── */
function LearnerDetailPanel({
  learner,
  enterpriseServices,
  onClose,
  onPhotoUpload,
  onPhotoRemove,
  onUpdate,
  onDelete,
  onDeactivate,
  uploading,
  photoError,
}: {
  learner: LearnerRow;
  enterpriseServices: EnterpriseServiceOption[];
  onClose: () => void;
  onPhotoUpload: (id: string, file: File) => void;
  onPhotoRemove: (id: string) => void;
  onUpdate: (
    id: string,
    data: Record<string, string | number | boolean | null | undefined | string[]>,
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDeactivate: (id: string, active: boolean) => Promise<void>;
  uploading: boolean;
  photoError?: string | null;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState(learner.firstName ?? '');
  const [editLastName, setEditLastName] = useState(learner.lastName ?? '');
  const [editEmail, setEditEmail] = useState(learner.email);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [infpc, setInfpc] = useState<InfpcFormState>(() => infpcFormFromLearner(learner));
  const [institutionServiceId, setInstitutionServiceId] = useState<string | null>(learner.institutionServiceId ?? null);
  const [companyArrivalDate, setCompanyArrivalDate] = useState(() =>
    parseInstitutionStoredDateToInputValue(learner.companyArrivalDate ?? ''),
  );
  const [infpcSaving, setInfpcSaving] = useState(false);
  const [infpcNotice, setInfpcNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [groupCatalog, setGroupCatalog] = useState<InstitutionConfiguredGroupRow[]>([]);
  const [editGroupIds, setEditGroupIds] = useState<string[]>(() => [...(learner.learningGroupIds ?? [])]);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupNotice, setGroupNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const raw = await api.get<unknown>('/institution/groups');
        if (!cancelled) setGroupCatalog(parseInstitutionGroupsApiResponse(raw));
      } catch {
        if (!cancelled) setGroupCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [learner.id]);

  useEffect(() => {
    setEditing(false);
    setEditFirstName(learner.firstName ?? '');
    setEditLastName(learner.lastName ?? '');
    setEditEmail(learner.email);
  }, [learner.id, learner.firstName, learner.lastName, learner.email]);

  useEffect(() => {
    setInfpc(infpcFormFromLearner(learner));
    setInstitutionServiceId(learner.institutionServiceId ?? null);
    setCompanyArrivalDate(parseInstitutionStoredDateToInputValue(learner.companyArrivalDate ?? ''));
    setInfpcNotice(null);
  }, [
    learner.id,
    learner.infpcGender,
    learner.infpcCsp,
    learner.infpcHasDiploma,
    learner.infpcSeniorityYears,
    learner.infpcIsSalariePlus,
    learner.infpcSalariePlusType,
    learner.infpcHireDate,
    learner.infpcBirthDate,
    learner.institutionServiceId,
    learner.service,
    learner.companyArrivalDate,
  ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(learner.id, { firstName: editFirstName, lastName: editLastName, email: editEmail });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInfpc = async () => {
    setInfpcSaving(true);
    setInfpcNotice(null);
    try {
      const sy = infpc.seniority.trim();
      let seniorityNum: number | null = null;
      if (sy !== '') {
        const n = Number.parseInt(sy, 10);
        if (!Number.isFinite(n) || n < 0) {
          setInfpcNotice({ type: 'err', text: t('institution.learners.infpc.invalidNumber', 'Nombre invalide (ancienneté)') });
          return;
        }
        seniorityNum = n;
      }
      await onUpdate(learner.id, {
        infpcGender: infpc.gender === '' ? null : infpc.gender,
        infpcCsp: infpc.csp === '' ? null : infpc.csp,
        infpcHasDiploma: infpc.hasDiploma,
        infpcSeniorityYears: seniorityNum,
        infpcIsSalariePlus: infpc.isSalariePlus,
        infpcSalariePlusType: infpc.salariePlusType === '' ? null : Number.parseInt(infpc.salariePlusType, 10),
        infpcHireDate: parseInstitutionStoredDateToInputValue(infpc.hireDate) || null,
        infpcBirthDate: parseInstitutionStoredDateToInputValue(infpc.birthDate) || null,
        institutionServiceId,
        companyArrivalDate: parseInstitutionStoredDateToInputValue(companyArrivalDate) || null,
      });
      setInfpcNotice({ type: 'ok', text: t('institution.learners.infpc.saved') });
    } catch {
      setInfpcNotice({ type: 'err', text: 'Échec de l’enregistrement' });
    } finally {
      setInfpcSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(learner.id);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const isActive = learner.status !== 'inactive';
  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await onDeactivate(learner.id, !isActive);
    } finally {
      setDeactivating(false);
      setShowDeactivateModal(false);
    }
  };

  const handleSaveGroups = async () => {
    setGroupSaving(true);
    setGroupNotice(null);
    try {
      await onUpdate(learner.id, { learningGroupIds: [...new Set(editGroupIds.map((id) => id.trim()).filter(Boolean))] });
      setGroupNotice({ type: 'ok', text: t('institution.learners.groupsSaved', 'Groupes enregistrés.') });
    } catch {
      setGroupNotice({ type: 'err', text: t('institution.learners.groupsSaveError', 'Impossible d’enregistrer les groupes.') });
    } finally {
      setGroupSaving(false);
    }
  };

  const lblStatus = (s: string) => {
    if (s === 'active') return t('institution.learners.statusActive');
    if (s === 'inactive') return t('institution.learners.statusInactive');
    if (s === 'completed') return t('institution.learners.statusCompleted');
    return s;
  };

  const serviceSelectOptions = useMemo(
    () => [
      { value: '', label: t('institution.learners.serviceNone') },
      ...[...enterpriseServices]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => ({ value: s.id, label: s.name })),
    ],
    [enterpriseServices, t],
  );

  /** Hors arbre du `motion.div` (InstitutionPage) : `position:fixed` sinon ancré au transform Framer. Modales incluses. */
  const portaled = (
    <>
      <div className="fixed inset-0 z-[90] bg-black/20 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div
        className="fixed inset-y-0 end-0 z-[110] flex w-full max-w-xl flex-col border-s border-[#E8EDF5] bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] animate-in duration-200 ltr:animate-slide-in-right rtl:animate-slide-in-left"
        role="dialog"
        aria-modal="true"
        aria-labelledby="learner-rh-sheet-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#F0F4FA] px-5 py-3 dark:border-[#122644]">
          <h2 id="learner-rh-sheet-title" className="text-sm font-semibold uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.learners.sheetTitle')}
          </h2>
          <div className="flex items-center gap-1">
            {!editing && (
              <button type="button" onClick={() => setEditing(true)} className="rounded-lg p-1.5 text-[#5B6472]/70 hover:bg-[#F0F4FA] hover:text-blue-600 dark:hover:bg-[#122644] transition-colors" title={t('institution.common.edit')}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-[#5B6472]/70 hover:bg-[#F0F4FA] hover:text-[#5B6472] dark:hover:bg-[#122644] dark:hover:text-[#5B6472]/50 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Identity */}
          <div className="flex gap-4 p-5">
            <div className="relative shrink-0 group">
              {learner.facePhotoUrl ? (
                <img src={learner.facePhotoUrl} alt={learner.name} className="h-20 w-20 rounded-xl border-2 border-[#E8EDF5] object-cover dark:border-[#19335E]" />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-xl border-2 border-dashed border-[#E8EDF5] bg-[#F8FAFC] dark:border-[#B6CAEC] dark:bg-[#122644]">
                  <Camera className="h-7 w-7 text-[#5B6472]/50 dark:text-[#B6CAEC]" />
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                ) : (
                  <>
                    <button type="button" onClick={() => fileRef.current?.click()} className="rounded-md bg-white/20 p-1.5 text-white hover:bg-white/30 transition-colors" title={learner.facePhotoUrl ? t('institution.learners.replacePhoto') : t('institution.learners.addPhoto')}>
                      <Camera className="h-4 w-4" />
                    </button>
                    {learner.facePhotoUrl && (
                      <button type="button" onClick={() => onPhotoRemove(learner.id)} className="rounded-md bg-white/20 p-1.5 text-white hover:bg-red-500/80 transition-colors" title={t('institution.learners.removePhoto')}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhotoUpload(learner.id, f); e.target.value = ''; }} />
            </div>

            <div className="min-w-0 flex-1 pt-1">
              {photoError && (
                <div className="mb-2 rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  {photoError}
                </div>
              )}
              {editing ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                    <Input
                      label={t('institution.learners.firstName')}
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      inputSize="sm"
                      autoComplete="given-name"
                      wrapperClassName="min-w-0 flex-1"
                      labelClassName="text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]"
                    />
                    <Input
                      label={t('institution.learners.lastName')}
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      inputSize="sm"
                      autoComplete="family-name"
                      wrapperClassName="min-w-0 flex-1"
                      labelClassName="text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]"
                    />
                  </div>
                  <Input
                    label={t('institution.common.email')}
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    inputSize="sm"
                    autoComplete="email"
                    wrapperClassName="min-w-0"
                    labelClassName="text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]"
                  />
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving} leftIcon={saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}>
                      {t('institution.learners.save')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditFirstName(learner.firstName ?? ''); setEditLastName(learner.lastName ?? ''); setEditEmail(learner.email); }}>
                      {t('institution.common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-lg font-bold text-[#0C1C38] dark:text-white">{learner.name}</h3>
                    <button
                      type="button"
                      onClick={() => setShowDeactivateModal(true)}
                      className={cn(
                        'shrink-0 rounded-md p-1 transition-colors',
                        learner.status === 'inactive'
                          ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                      )}
                      title={learner.status === 'inactive' ? t('institution.learners.reactivate') : t('institution.learners.deactivate')}
                    >
                      <Ban className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="truncate text-sm text-[#5B6472] dark:text-[#B6CAEC]">{learner.email}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant={statusBadge(learner.status)}>{lblStatus(learner.status)}</Badge>
                    {learner.group !== '—' && <Badge variant="info">{learner.group}</Badge>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 px-5 pb-4">
            {[
              { icon: BookOpen, label: t('institution.learners.flows'), value: String(learner.enrollments), color: 'text-blue-500' },
              { icon: CheckCircle2, label: t('institution.learners.completedF'), value: String(learner.completedCount), color: 'text-[#19335E]' },
              { icon: Clock, label: t('institution.learners.totalTime'), value: formatDuration(learner.totalTimeSpent), color: 'text-amber-500' },
              { icon: BarChart3, label: t('institution.learners.progress'), value: `${learner.progress}%`, color: 'text-violet-500' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3 rounded-lg border border-[#F0F4FA] bg-[#F8FAFC]/50 px-3 py-2.5 dark:border-[#122644] dark:bg-[#122644]/50">
                <s.icon className={cn('h-4 w-4 shrink-0', s.color)} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0C1C38] dark:text-white">{s.value}</p>
                  <p className="text-[11px] text-[#5B6472] dark:text-[#B6CAEC]">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mx-5 mb-4 rounded-xl border border-[#E8EDF5] bg-white/90 p-4 shadow-sm dark:border-[#19335E] dark:bg-[#061128]/40">
            <div className="mb-2 flex items-center gap-2">
              <Users className="h-4 w-4 text-[#5B6472] dark:text-[#B6CAEC]" aria-hidden />
              <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white">{t('institution.learners.colGroup')}</h3>
            </div>
            <p className="mb-2 text-[11px] leading-snug text-[#5B6472] dark:text-[#B6CAEC]">
              {t(
                'institution.learners.groupsPickHint',
                'Cochez un ou plusieurs groupes, puis enregistrez. La liste provient de l’onglet « Groupes ».',
              )}
            </p>
            {groupNotice && (
              <div
                className={cn(
                  'mb-2 rounded-md px-2.5 py-1.5 text-xs',
                  groupNotice.type === 'ok'
                    ? 'bg-[#F0F4FA] text-[#0C1C38] dark:bg-[#F8FAFC]/25 dark:text-[#19335E]'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                )}
              >
                {groupNotice.text}
              </div>
            )}
            <InstitutionConfiguredGroupsList
              groups={groupCatalog}
              interactive
              selectedGroupIds={editGroupIds}
              onSelectedGroupIdsChange={setEditGroupIds}
            />
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={groupSaving}
                onClick={() => void handleSaveGroups()}
                leftIcon={groupSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              >
                {t('institution.learners.saveGroups', 'Enregistrer les groupes')}
              </Button>
            </div>
          </div>

          <div className="mx-5 mb-4 rounded-xl border border-[#B6CAEC]/80 bg-[#F0F4FA]/40 p-4 dark:border-[#F8FAFC]/50 dark:bg-[#F8FAFC]/20">
            <div className="mb-3 flex items-center gap-2">
              <User className="h-4 w-4 text-[#19335E] dark:text-[#92B5FF]" aria-hidden />
              <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white">{t('institution.learners.infpc.sectionTitle')}</h3>
            </div>
            <p className="mb-3 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.learners.infpc.sectionHint')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <InstitutionStyledSelect
                panelZIndexClass="z-[150]"
                label={t('institution.learners.infpc.gender')}
                options={[
                  { value: '', label: t('institution.learners.infpc.genderPlaceholder') },
                  { value: 'M', label: t('institution.learners.infpc.genderM') },
                  { value: 'F', label: t('institution.learners.infpc.genderF') },
                ]}
                value={infpc.gender}
                onChange={(v) => setInfpc((p) => ({ ...p, gender: v as InfpcFormState['gender'] }))}
              />
              <InstitutionStyledSelect
                panelZIndexClass="z-[150]"
                label={t('institution.learners.infpc.csp')}
                options={[
                  { value: '', label: t('institution.learners.infpc.cspPlaceholder') },
                  ...INFPC_CSP_VALUES.map((v) => ({ value: v, label: t(`institution.learners.infpc.csp.${v}`) })),
                ]}
                value={infpc.csp}
                onChange={(v) => setInfpc((p) => ({ ...p, csp: v }))}
              />
              <div className="flex cursor-pointer items-center gap-2 pt-6 text-xs text-[#0C1C38] dark:text-[#DCE7F7] sm:col-span-2">
                <Switch
                  size="sm"
                  checked={infpc.hasDiploma}
                  onChange={(checked) => setInfpc((p) => ({ ...p, hasDiploma: checked }))}
                />
                <span>{t('institution.learners.infpc.hasDiploma')}</span>
              </div>
              <Input
                type="number"
                min={0}
                label={t('institution.learners.infpc.seniority')}
                value={infpc.seniority}
                onChange={(e) => setInfpc((p) => ({ ...p, seniority: e.target.value }))}
                inputSize="sm"
                wrapperClassName="min-w-0"
                labelClassName="text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]"
              />
              <label className="block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]">
                {t('institution.learners.infpc.birthDate')}
                <InstitutionStyledTemporalInput
                  variant="date"
                  wrapperClassName="mt-1"
                  value={infpc.birthDate}
                  onChange={(e) => setInfpc((p) => ({ ...p, birthDate: e.target.value }))}
                />
              </label>
              <div className="min-w-0">
                <span className="mb-1.5 block text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.learners.infpc.age', 'Âge')}
                </span>
                <p className="rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm font-medium text-[#0C1C38] dark:border-[#B6CAEC] dark:bg-[#0C1C38] dark:text-white">
                  {computeInfpcAgeFromBirthDateYmd(
                    parseInstitutionStoredDateToInputValue(infpc.birthDate) || null,
                  ) ?? '—'}
                </p>
                <p className="mt-1 text-[10px] leading-snug text-[#5B6472] dark:text-[#B6CAEC]">
                  {t(
                    'institution.learners.infpc.ageFromBirthHint',
                    'Calculé automatiquement à partir de la date de naissance.',
                  )}
                </p>
              </div>
              <div className="flex cursor-pointer items-center gap-2 pt-6 text-xs text-[#0C1C38] dark:text-[#DCE7F7]">
                <Switch
                  size="sm"
                  checked={infpc.isSalariePlus}
                  onChange={(checked) => setInfpc((p) => ({ ...p, isSalariePlus: checked }))}
                />
                <span>{t('institution.learners.infpc.salariePlus')}</span>
              </div>
              <InstitutionStyledSelect
                panelZIndexClass="z-[150]"
                label={t('institution.learners.infpc.salariePlusType')}
                disabled={!infpc.isSalariePlus}
                options={[
                  { value: '', label: t('institution.learners.infpc.salariePlusTypePlaceholder') },
                  { value: '1', label: t('institution.learners.infpc.salariePlusType1') },
                  { value: '2', label: t('institution.learners.infpc.salariePlusType2') },
                ]}
                value={infpc.salariePlusType}
                onChange={(v) => setInfpc((p) => ({ ...p, salariePlusType: v as InfpcFormState['salariePlusType'] }))}
              />
              <Select
                label={t('institution.learners.service')}
                helperText={t('institution.learners.serviceHint')}
                selectSize="sm"
                wrapperClassName="sm:col-span-2"
                disabled={infpcSaving}
                value={institutionServiceId ?? ''}
                onChange={(e) =>
                  setInstitutionServiceId(e.target.value === '' ? null : e.target.value)
                }
                options={serviceSelectOptions}
              />
              <label className="block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]">
                {t('institution.learners.companyArrivalDate', "Date d'arrivée dans l'entreprise")}
                <InstitutionStyledTemporalInput
                  variant="date"
                  wrapperClassName="mt-1"
                  value={companyArrivalDate}
                  onChange={(e) => setCompanyArrivalDate(e.target.value)}
                />
              </label>
              <label className="block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]">
                {t('institution.learners.infpc.hireDate')}
                <InstitutionStyledTemporalInput
                  variant="date"
                  wrapperClassName="mt-1"
                  value={infpc.hireDate}
                  onChange={(e) => setInfpc((p) => ({ ...p, hireDate: e.target.value }))}
                />
              </label>
            </div>
            {infpcNotice && (
              <p
                className={cn(
                  'mt-3 text-xs',
                  infpcNotice.type === 'ok' ? 'text-[#19335E] dark:text-[#92B5FF]' : 'text-red-600 dark:text-red-400',
                )}
              >
                {infpcNotice.text}
              </p>
            )}
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleSaveInfpc()}
                disabled={infpcSaving}
                leftIcon={infpcSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              >
                {t('institution.learners.infpc.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-[#F0F4FA] px-5 py-2.5 mb-3 dark:border-[#122644]">
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
            title={t('institution.learners.deleteLearner')}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      <ConfirmModal
        open={showDeleteModal}
        title={t('institution.learners.deleteTitle')}
        description={t('institution.learners.deleteDesc', { name: learner.name, email: learner.email })}
        confirmLabel={t('institution.common.delete')}
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      <ConfirmModal
        open={showDeactivateModal}
        title={isActive ? t('institution.learners.deactivateTitle') : t('institution.learners.reactivateTitle')}
        description={isActive
          ? t('institution.learners.deactivateDesc', { name: learner.name })
          : t('institution.learners.reactivateDesc', { name: learner.name })}
        confirmLabel={isActive ? t('institution.learners.deactivate') : t('institution.learners.reactivate')}
        danger={isActive}
        loading={deactivating}
        onConfirm={handleDeactivate}
        onCancel={() => setShowDeactivateModal(false)}
      />
    </>
  );

  return typeof document !== 'undefined' ? createPortal(portaled, document.body) : null;
}

/* ────────────────────────────────────────────────────────────────────────────
   Main Component
   ──────────────────────────────────────────────────────────────────────────── */
export default function LearnerManagement() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const statusOptions = useMemo(
    () => [
      { value: '', label: t('institution.learners.statusAll') },
      { value: 'active', label: t('institution.learners.statusActive') },
      { value: 'inactive', label: t('institution.learners.statusInactive') },
      { value: 'completed', label: t('institution.learners.statusCompleted') },
    ],
    [t],
  );
  const learnerStatusLabel = useCallback(
    (s: string) => {
      if (s === 'active') return t('institution.learners.statusActive');
      if (s === 'inactive') return t('institution.learners.statusInactive');
      if (s === 'completed') return t('institution.learners.statusCompleted');
      return s;
    },
    [t],
  );
  const user = useAuthStore((s) => s.user);
  const canManageFaceReference = ['institution_admin', 'drh', 'rh'].includes(String(user?.role ?? ''));
  const [learners, setLearners] = useState<LearnerRow[]>([]);
  const [enterpriseServices, setEnterpriseServices] = useState<EnterpriseServiceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  /** Panneau latéral : photo, identité, INFPC, suppression (ouvert explicitement). */
  const [sheetLearnerId, setSheetLearnerId] = useState<string | null>(null);

  const loadLearners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<any>('/institution/learners', { page: 1, limit: 500 });
      const raw = response?.data ?? response;
      const rows = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
      const mapped: LearnerRow[] = rows.map((l: any) => ({
        id: l.id,
        firstName: l.firstName,
        lastName: l.lastName,
        name: [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email,
        email: l.email,
        group: l.group || '—',
        progress: l.progress ?? 0,
        enrollments: l.enrollments ?? 0,
        completedCount: l.completedCount ?? 0,
        totalTimeSpent: l.totalTimeSpent ?? 0,
        lastActive: l.lastActive || l.lastLoginAt || null,
        status: l.status || 'inactive',
        facePhotoUrl: l.facePhotoUrl ?? null,
        infpcGender: l.infpcGender ?? null,
        infpcCsp: l.infpcCsp ?? null,
        infpcHasDiploma: l.infpcHasDiploma ?? null,
        infpcSeniorityYears: l.infpcSeniorityYears ?? null,
        infpcAge: l.infpcAge ?? null,
        infpcIsSalariePlus: l.infpcIsSalariePlus ?? null,
        infpcSalariePlusType: l.infpcSalariePlusType ?? null,
        infpcHireDate: l.infpcHireDate ?? null,
        infpcBirthDate: l.infpcBirthDate ?? null,
        service: l.service ?? null,
        institutionServiceId: l.institutionServiceId ?? null,
        companyArrivalDate: l.companyArrivalDate ?? null,
        learningGroupIds: Array.isArray(l.learningGroupIds)
          ? l.learningGroupIds.map((id: unknown) => String(id))
          : [],
        flows: l.flows ?? [],
      }));
      setLearners(mapped);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLearners();
  }, [loadLearners]);

  const loadEnterpriseServices = useCallback(async () => {
    try {
      const resp = await api.get<{ data?: { data?: unknown[] } } & { data?: unknown[] }>('/institution/services');
      const top = resp as { data?: unknown };
      const inner = top?.data;
      const arr = Array.isArray(inner)
        ? inner
        : inner && typeof inner === 'object' && 'data' in inner && Array.isArray((inner as { data: unknown[] }).data)
          ? (inner as { data: unknown[] }).data
          : [];
      setEnterpriseServices(
        arr
          .filter((x): x is { id: string; name: string } => Boolean(x && typeof x === 'object' && 'id' in x && 'name' in x))
          .map((x) => ({ id: String(x.id), name: String(x.name) })),
      );
    } catch {
      setEnterpriseServices([]);
    }
  }, []);

  useEffect(() => {
    void loadEnterpriseServices();
  }, [loadEnterpriseServices]);

  const [photoError, setPhotoError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addSendInvitation, setAddSendInvitation] = useState(true);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const openAddModal = () => {
    setAddFirstName('');
    setAddLastName('');
    setAddEmail('');
    setAddSendInvitation(true);
    setAddError(null);
    setShowAddModal(true);
  };

  const submitAddLearner = async () => {
    setAddSaving(true);
    setAddError(null);
    try {
      await api.post('/institution/learners', {
        firstName: addFirstName.trim(),
        lastName: addLastName.trim(),
        email: addEmail.trim(),
        sendInvitation: addSendInvitation,
      });
      setShowAddModal(false);
      await loadLearners();
    } catch (e: unknown) {
      setAddError(e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : t('institution.learners.addError'));
    } finally {
      setAddSaving(false);
    }
  };

  const parseBool = (v: unknown): boolean | null | undefined => {
    if (v == null || String(v).trim() === '') return undefined;
    const s = String(v).trim().toLowerCase();
    if (['1', 'true', 'oui', 'yes', 'y'].includes(s)) return true;
    if (['0', 'false', 'non', 'no', 'n'].includes(s)) return false;
    return undefined;
  };

  const parseNullableInt = (v: unknown): number | null | undefined => {
    if (v == null || String(v).trim() === '') return undefined;
    const n = Number.parseInt(String(v).trim(), 10);
    if (!Number.isFinite(n)) return undefined;
    return n;
  };

  const handleDownloadCsvTemplate = useCallback(() => {
    const headers = [
      'firstName',
      'lastName',
      'email',
      'sendInvitation',
      'institutionService',
      'companyArrivalDate',
      'infpcGender',
      'infpcCsp',
      'infpcHasDiploma',
      'infpcSeniorityYears',
      'infpcIsSalariePlus',
      'infpcSalariePlusType',
      'infpcHireDate',
      'infpcBirthDate',
    ];
    const example = [
      'Jean',
      'Dupont',
      'jean.dupont@example.com',
      'true',
      'Service Commercial',
      '2023-09-01',
      'M',
      'employee',
      'true',
      '3',
      'false',
      '',
      '01/09/2023',
      '12/03/1995',
    ];
    const csv = `${headers.join(';')}\n${example.join(';')}\n`;
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-import-apprenants.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const handleCsvImportFile = useCallback(
    async (file: File) => {
      setCsvImporting(true);
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<CsvImportRow>(ws, { defval: '' });
        if (rows.length === 0) return;

        const serviceByNormalized = new Map(
          enterpriseServices.map((s) => [normalizeHeaderKey(s.name), s.id]),
        );

        let created = 0;
        for (const row of rows) {
          const normalizedRow = new Map<string, unknown>();
          for (const [k, v] of Object.entries(row)) normalizedRow.set(normalizeHeaderKey(k), v);
          const firstName = String(
            normalizedRow.get('firstname') ?? normalizedRow.get('prenom') ?? '',
          ).trim();
          const lastName = String(
            normalizedRow.get('lastname') ?? normalizedRow.get('nom') ?? '',
          ).trim();
          const email = String(normalizedRow.get('email') ?? '').trim().toLowerCase();
          if (!firstName || !lastName || !email) continue;

          const serviceRaw = String(
            normalizedRow.get('institutionservice') ??
              normalizedRow.get('service') ??
              normalizedRow.get('department') ??
              '',
          ).trim();
          const serviceId = serviceRaw
            ? serviceByNormalized.get(normalizeHeaderKey(serviceRaw)) ?? null
            : null;

          const gender = String(normalizedRow.get('infpcgender') ?? '').trim().toUpperCase();
          const csp = String(normalizedRow.get('infpccsp') ?? '').trim();
          const payload: Record<string, unknown> = {
            firstName,
            lastName,
            email,
            sendInvitation:
              parseBool(normalizedRow.get('sendinvitation')) ??
              parseBool(normalizedRow.get('sendinvitationemail')) ??
              true,
            institutionServiceId: serviceId,
            companyArrivalDate:
              String(normalizedRow.get('companyarrivaldate') ?? '').trim() || null,
            infpcGender: gender === 'M' || gender === 'F' ? gender : null,
            infpcCsp: csp || null,
            infpcHasDiploma: parseBool(normalizedRow.get('infpchasdiploma')) ?? null,
            infpcSeniorityYears: parseNullableInt(normalizedRow.get('infpcseniorityyears')) ?? null,
            infpcIsSalariePlus: parseBool(normalizedRow.get('infpcissalarieplus')) ?? null,
            infpcSalariePlusType:
              parseNullableInt(normalizedRow.get('infpcsalarieplustype')) ?? null,
            infpcHireDate: String(normalizedRow.get('infpchiredate') ?? '').trim() || null,
            infpcBirthDate: String(normalizedRow.get('infpcbirthdate') ?? '').trim() || null,
          };
          try {
            await api.post('/institution/learners', payload);
            created += 1;
          } catch {
            // Ignore row errors, continue import.
          }
        }
        await loadLearners();
        if (created === 0) {
          setAddError(t('institution.learners.importNoRows', 'Aucune ligne importable trouvée.'));
        } else {
          setAddError(null);
        }
      } finally {
        setCsvImporting(false);
      }
    },
    [enterpriseServices, loadLearners, t],
  );

  const handleFaceRefFile = async (learnerId: string, file: File) => {
    setUploadingId(learnerId);
    setPhotoError(null);
    try {
      await api.upload(`/institution/learners/${learnerId}/face-reference-photo`, file);
      await loadLearners();
    } catch (err: any) {
      setPhotoError(err?.message ?? t('institution.learners.photoUploadError'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveFaceRef = async (learnerId: string) => {
    setUploadingId(learnerId);
    setPhotoError(null);
    try {
      await api.delete(`/institution/learners/${learnerId}/face-reference-photo`);
      await loadLearners();
    } catch (err: any) {
      setPhotoError(err?.message ?? t('institution.learners.photoRemoveError'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleUpdateLearner = async (
    id: string,
    data: Record<string, string | number | boolean | null | undefined | string[]>,
  ) => {
    await api.patch(`/institution/learners/${id}`, sanitizeInstitutionLearnerPatch(data));
    await loadLearners();
  };

  const handleDeleteLearner = async (id: string) => {
    await api.delete(`/institution/learners/${id}`);
    setDetailId(null);
    setSheetLearnerId(null);
    await loadLearners();
  };

  const handleDeactivateLearner = async (id: string, activate: boolean) => {
    await api.patch(`/institution/learners/${id}`, { status: activate ? 'active' : 'inactive' });
    await loadLearners();
  };

  const filtered = useMemo(() => {
    let list = [...learners];
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter((l) => l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q));
    }
    if (statusFilter) list = list.filter((l) => l.status === statusFilter);
    list.sort((a, b) => {
      const av = (a as any)[sortField] ?? '';
      const bv = (b as any)[sortField] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [debouncedSearch, learners, sortDir, sortField, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const allSelected = paginated.length > 0 && paginated.every((l) => selected.has(l.id));

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const totalEnrollments = learners.reduce((s, l) => s + l.enrollments, 0);
  const totalCompleted = learners.reduce((s, l) => s + l.completedCount, 0);
  const activeCount = learners.filter((l) => l.status === 'active').length;

  const sheetLearner = sheetLearnerId ? learners.find((l) => l.id === sheetLearnerId) ?? null : null;

  const exportToExcel = useCallback(
    (rows: LearnerRow[]) => {
      const K = {
        fn: t('institution.learners.export.firstName'),
        ln: t('institution.learners.export.lastName'),
        em: t('institution.common.email'),
        gr: t('institution.learners.export.groupCol'),
        enr: t('institution.learners.export.enrollments'),
        cmp: t('institution.learners.export.completed'),
        prg: t('institution.learners.export.progressPct'),
        tmin: t('institution.learners.export.totalMin'),
        lact: t('institution.learners.export.lastAct'),
        st: t('institution.learners.export.statusCol'),
        lr: t('institution.learners.export.learnerCol'),
        ty: t('institution.learners.export.typeCol'),
        ti: t('institution.learners.export.titleCol'),
        bl: t('institution.learners.export.blocks'),
        ss: t('institution.learners.export.sessionsDone'),
        av: t('institution.learners.export.avgScore'),
        tm: t('institution.learners.export.timeMin'),
        ds: t('institution.learners.export.dateStart'),
        de: t('institution.learners.export.dateEnd'),
        la: t('institution.learners.export.lastAcc'),
        tc: t('institution.learners.export.typeCourse'),
        tp: t('institution.learners.export.typePath'),
      };
      const flowDone = t('institution.learners.flowDone');
      const inProg = t('institution.learners.inProgress');
      const pathDone = t('institution.learners.done');

      const summaryRows = rows.map((l) => ({
        [K.fn]: l.firstName ?? '',
        [K.ln]: l.lastName ?? '',
        [K.em]: l.email,
        [K.gr]: l.group,
        [K.enr]: l.enrollments,
        [K.cmp]: l.completedCount,
        [K.prg]: l.progress,
        [K.tmin]: Math.round(l.totalTimeSpent / 60),
        [K.lact]: l.lastActive ? new Date(l.lastActive).toLocaleDateString(dateLocale) : '',
        [K.st]: learnerStatusLabel(l.status),
      }));

      const detailRows: Record<string, unknown>[] = [];
      rows.forEach((l) => {
        (l.flows ?? []).forEach((f) => {
          detailRows.push({
            [K.lr]: l.name,
            [K.em]: l.email,
            [K.ty]: K.tc,
            [K.ti]: f.title,
            [K.st]: f.completed ? flowDone : inProg,
            [K.bl]: `${f.blocksVisited ?? 0}/${f.totalBlocks ?? 0}`,
            [K.ss]: f.sessionsCompleted ?? 0,
            [K.av]: f.averageScore ?? '',
            [K.tm]: Math.round((f.totalTimeSpent ?? 0) / 60),
            [K.ds]: f.startedAt ? new Date(f.startedAt).toLocaleDateString(dateLocale) : '',
            [K.de]: f.completedAt ? new Date(f.completedAt).toLocaleDateString(dateLocale) : '',
            [K.la]: f.lastAccessAt ? new Date(f.lastAccessAt).toLocaleDateString(dateLocale) : '',
          });
        });
      });

      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      const colWidths = Object.keys(summaryRows[0] ?? {}).map((k) => ({ wch: Math.max(k.length, 14) }));
      wsSummary['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, wsSummary, t('institution.learners.exportSheetLearners'));

      if (detailRows.length > 0) {
        const wsDetail = XLSX.utils.json_to_sheet(detailRows);
        const detailWidths = Object.keys(detailRows[0] ?? {}).map((k) => ({ wch: Math.max(k.length, 14) }));
        wsDetail['!cols'] = detailWidths;
        XLSX.utils.book_append_sheet(wb, wsDetail, t('institution.learners.exportSheetDetails'));
      }

      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `learners-export-${date}.xlsx`);
    },
    [t, dateLocale, learnerStatusLabel],
  );

  const handleExportAll = () => exportToExcel(filtered);
  const handleExportSelected = () => {
    const rows = learners.filter((l) => selected.has(l.id));
    if (rows.length > 0) exportToExcel(rows);
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.learners.title')}</h1>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.learners.subtitle', { count: learners.length })}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleCsvImportFile(f);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            leftIcon={csvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            size="sm"
            onClick={() => csvInputRef.current?.click()}
            disabled={csvImporting}
          >
            {t('institution.learners.importCsv')}
          </Button>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            size="sm"
            onClick={handleDownloadCsvTemplate}
            disabled={csvImporting}
          >
            {t('institution.learners.downloadTemplate', 'Template CSV')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportAll} disabled={filtered.length === 0} leftIcon={<Download className="h-4 w-4" />}>
            {t('institution.learners.exportAll')}
          </Button>
          {selected.size > 0 ? (
            <>
              <span className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.learners.selectedCount', { count: selected.size })}</span>
              <Button variant="outline" size="sm" onClick={handleExportSelected} leftIcon={<FileSpreadsheet className="h-4 w-4" />}>
                {t('institution.learners.exportSelection')}
              </Button>
            </>
          ) : null}
          <Button variant="primary" leftIcon={<UserPlus className="h-4 w-4" />} size="sm" onClick={openAddModal}>
            {t('institution.learners.add')}
          </Button>
        </div>
      </div>
      {addError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {addError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {[
          { label: t('institution.learners.statLearners'), value: learners.length, icon: UserPlus, color: 'text-blue-600 dark:text-blue-400' },
          { label: t('institution.learners.statActive30'), value: activeCount, icon: Clock, color: 'text-green-600 dark:text-green-400' },
          { label: t('institution.learners.statEnrollments'), value: totalEnrollments, icon: BookOpen, color: 'text-purple-600 dark:text-purple-400' },
          { label: t('institution.learners.statCompleted'), value: totalCompleted, icon: CheckCircle2, color: 'text-[#19335E] dark:text-[#5C82C5]' },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-4 rounded-xl border border-[#E8EDF5] bg-white p-4 dark:border-[#19335E] dark:bg-[#122644]">
            <div className={cn('rounded-lg bg-[#F8FAFC] p-2 dark:bg-[#19335E]/50', s.color)}><s.icon className="h-5 w-5" /></div>
            <div>
              <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{s.value}</p>
              <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="min-w-0 w-full flex-1 sm:min-w-[10rem]">
          <InstitutionStyledSearchInput
            placeholder={t('institution.learners.searchPh')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
              setDetailId(null);
            }}
            showClear
            onClear={() => {
              setSearch('');
              setPage(1);
              setDetailId(null);
            }}
            aria-label={t('institution.learners.searchPh')}
          />
        </div>
        <div className="w-full shrink-0 sm:w-44 sm:min-w-[11rem]">
          <InstitutionStyledSelect
            panelZIndexClass="z-[130]"
            label={t('institution.learners.filterStatus')}
            options={statusOptions}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
              setDetailId(null);
            }}
          />
        </div>
      </div>

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Switch
                size="sm"
                checked={allSelected}
                onChange={(checked) => {
                  const next = new Set(selected);
                  if (checked) paginated.forEach((l) => next.add(l.id));
                  else paginated.forEach((l) => next.delete(l.id));
                  setSelected(next);
                }}
              />
            </TableHead>
            <TableHead sortable sortDirection={sortField === 'name' ? sortDir : undefined} onSort={() => handleSort('name')}>{t('institution.learners.colLearner')}</TableHead>
            <TableHead sortable sortDirection={sortField === 'email' ? sortDir : undefined} onSort={() => handleSort('email')}>{t('institution.common.email')}</TableHead>
            <TableHead>{t('institution.learners.colGroup')}</TableHead>
            <TableHead sortable sortDirection={sortField === 'enrollments' ? sortDir : undefined} onSort={() => handleSort('enrollments')}>{t('institution.learners.flows')}</TableHead>
            <TableHead sortable sortDirection={sortField === 'progress' ? sortDir : undefined} onSort={() => handleSort('progress')}>{t('institution.learners.progress')}</TableHead>
            <TableHead sortable sortDirection={sortField === 'lastActive' ? sortDir : undefined} onSort={() => handleSort('lastActive')}>{t('institution.learners.colLastActive')}</TableHead>
            <TableHead>{t('institution.learners.colStatus')}</TableHead>
            <TableHead className="w-[100px] text-end">{t('institution.common.actions', 'Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#5B6472]/70">{t('institution.common.loading')}</TableCell></TableRow>
          ) : paginated.length === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#5B6472]/70">{t('institution.learners.empty')}</TableCell></TableRow>
          ) : paginated.map((l) => (
              <TableRow
                key={l.id}
                className={cn(selected.has(l.id) && 'bg-[#F0F4FA] dark:bg-[#061128]/20')}
              >
                <TableCell>
                  <Switch
                    size="sm"
                    checked={selected.has(l.id)}
                    onChange={(checked) => {
                      const next = new Set(selected);
                      if (checked) next.add(l.id);
                      else next.delete(l.id);
                      setSelected(next);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    {l.facePhotoUrl ? (
                      <img src={l.facePhotoUrl} alt="" className="h-8 w-8 rounded-full border border-[#E8EDF5] object-cover dark:border-[#19335E]" />
                    ) : (
                      <Avatar name={l.name} size="sm" />
                    )}
                    <span className="font-medium text-[#0C1C38] dark:text-white">{l.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[#5B6472] dark:text-[#B6CAEC]">{l.email}</TableCell>
                <TableCell><Badge variant="info">{l.group}</Badge></TableCell>
                <TableCell className="text-center"><span className="text-sm font-medium">{l.completedCount}/{l.enrollments}</span></TableCell>
                <TableCell><Progress value={l.progress} max={100} size="sm" showLabel /></TableCell>
                <TableCell className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{l.lastActive ? new Date(l.lastActive).toLocaleDateString(dateLocale) : t('institution.common.empty')}</TableCell>
                <TableCell><Badge variant={statusBadge(l.status)}>{learnerStatusLabel(l.status)}</Badge></TableCell>
                <TableCell className="text-end">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => setSheetLearnerId(l.id)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E8EDF5] bg-white text-[#5B6472] transition hover:border-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] hover:text-[color:var(--inst-player-primary-deep,#19335E)] dark:border-[#19335E] dark:bg-[#122644] dark:text-[#B6CAEC]"
                      title={t('institution.learners.openProfileDetail')}
                      aria-label={t('institution.learners.openProfileDetail')}
                    >
                      <User className="h-4 w-4" aria-hidden />
                    </button>
                    <Link
                      to={institutionLearnerDetailPath(l.id, pathname)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E8EDF5] bg-white text-[#5B6472] transition hover:border-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] hover:text-[color:var(--inst-player-primary-deep,#19335E)] dark:border-[#19335E] dark:bg-[#122644] dark:text-[#B6CAEC]"
                      title={t('pages.learners.openProgressDashboard', 'Tableau de bord d’avancement')}
                      aria-label={t('pages.learners.openProgressDashboard', 'Tableau de bord d’avancement')}
                    >
                      <BarChart3 className="h-4 w-4" aria-hidden />
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between text-sm text-[#5B6472] dark:text-[#B6CAEC]">
        <span>{t('institution.learners.countList', { count: filtered.length })}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="h-4 w-4" /></Button>
          <span>{t('institution.common.page')} {page} {t('institution.common.of')} {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {sheetLearner && (
        <LearnerDetailPanel
          key={sheetLearner.id}
          learner={sheetLearner}
          enterpriseServices={enterpriseServices}
          onClose={() => { setSheetLearnerId(null); setPhotoError(null); }}
          onPhotoUpload={handleFaceRefFile}
          onPhotoRemove={handleRemoveFaceRef}
          onUpdate={handleUpdateLearner}
          onDelete={handleDeleteLearner}
          onDeactivate={handleDeactivateLearner}
          uploading={uploadingId === sheetLearner.id}
          photoError={photoError}
        />
      )}

      <Modal open={showAddModal} onClose={() => !addSaving && setShowAddModal(false)} size="md" ariaLabelledBy="learner-add-modal-title">
        <ModalHeader>
          <h2 id="learner-add-modal-title" className="text-lg font-semibold text-[#0C1C38] dark:text-white">
            {t('institution.learners.addModalTitle')}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.learners.addModalHint')}</p>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <Input
            label={t('institution.learners.firstName')}
            value={addFirstName}
            onChange={(e) => setAddFirstName(e.target.value)}
            inputSize="sm"
            autoComplete="given-name"
            disabled={addSaving}
          />
          <Input
            label={t('institution.learners.lastName')}
            value={addLastName}
            onChange={(e) => setAddLastName(e.target.value)}
            inputSize="sm"
            autoComplete="family-name"
            disabled={addSaving}
          />
          <Input
            label={t('institution.learners.addEmail')}
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            inputSize="sm"
            autoComplete="email"
            disabled={addSaving}
          />
          <div className="flex cursor-pointer items-start gap-2 text-sm text-[#121826] dark:text-[#DCE7F7]">
            <Switch
              size="sm"
              className="mt-0.5"
              checked={addSendInvitation}
              onChange={(checked) => setAddSendInvitation(checked)}
              disabled={addSaving}
            />
            <span>{t('institution.learners.addSendInvitation')}</span>
          </div>
          {addError ? <p className="text-sm text-red-600 dark:text-red-400">{addError}</p> : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={() => setShowAddModal(false)} disabled={addSaving}>
            {t('institution.common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void submitAddLearner()}
            disabled={addSaving || !addFirstName.trim() || !addLastName.trim() || !addEmail.trim()}
            leftIcon={addSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
          >
            {t('institution.learners.addSubmit')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
